import React, { createContext, useState, useEffect, useContext } from 'react'
import { alpha, createTheme } from '@mui/material'
import { ThemeProvider as MUIThemeProvider, GlobalStyles } from '@mui/material'
import { Storage } from '../../utils.jsx'

export const ThemeContext = createContext()

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(false)
  const [colorBlindMode, setColorBlindMode] = useState(false)
  const [contrastMode, setContrastMode] = useState(false)

  useEffect(() => {
    // Read values from local storage and set to state
    const dark = Storage.getBoolFromLocalStorage('darkMode')
    const colorBlind = Storage.getBoolFromLocalStorage('colorBlindMode')
    const contrast = Storage.getBoolFromLocalStorage('contrastMode')

    setDarkMode(dark)
    setColorBlindMode(colorBlind)
    setContrastMode(contrast)
  }, [])

  let currentTheme = lightTheme

  if (darkMode) {
    if (contrastMode) {
      currentTheme = contrastDarkTheme
    } else if (colorBlindMode) {
      currentTheme = colorBlindDarkTheme
    } else {
      currentTheme = darkTheme
    }
  } else {
    if (contrastMode) {
      currentTheme = contrastLightTheme
    } else if (colorBlindMode) {
      currentTheme = colorBlindLightTheme
    }
  }

  const toggleDarkMode = () => {
    setDarkMode((prevDarkMode) => {
      const newValue = !prevDarkMode
      Storage.saveBoolToLocalStorage('darkMode', newValue)
      return newValue
    })
  }

  const toggleColorBlindMode = () => {
    setColorBlindMode((prevColorBlindMode) => {
      const newValue = !prevColorBlindMode
      Storage.saveBoolToLocalStorage('colorBlindMode', newValue)

      // If turning on colorBlindMode, turn off contrastMode
      if (newValue) {
        setContrastMode(false)
        Storage.saveBoolToLocalStorage('contrastMode', false)
      }

      return newValue
    })
  }

  const toggleContrastMode = () => {
    setContrastMode((prevContrastMode) => {
      const newValue = !prevContrastMode
      Storage.saveBoolToLocalStorage('contrastMode', newValue)

      // If turning on contrastMode, turn off colorBlindMode
      if (newValue) {
        setColorBlindMode(false)
        Storage.saveBoolToLocalStorage('colorBlindMode', false)
      }

      return newValue
    })
  }

  useEffect(() => {
    document.body.style.backgroundColor =
      currentTheme.palette.background.default

    return () => {
      document.body.style.backgroundColor = ''
    }
  }, [currentTheme])

  return (
    <ThemeContext.Provider
      value={{
        darkMode,
        toggleDarkMode,
        colorBlindMode,
        toggleColorBlindMode,
        contrastMode,
        toggleContrastMode,
      }}
    >
      <MUIThemeProvider theme={currentTheme}>
        <GlobalStyles
          styles={{
            '*': { boxSizing: 'border-box' },
            'html, body, #root': { minHeight: '100%' },
            body: {
              margin: 0,
              minWidth: '320px',
              fontFamily: currentTheme.typography.fontFamily,
              backgroundColor: currentTheme.palette.background.default,
              backgroundImage: `radial-gradient(circle at 8% 0%, ${alpha(
                currentTheme.palette.primary.main,
                0.16,
              )}, transparent 34%), radial-gradient(circle at 92% 12%, ${alpha(
                currentTheme.palette.secondary.main,
                0.14,
              )}, transparent 30%)`,
              backgroundAttachment: 'fixed',
              color: currentTheme.palette.text.primary,
            },
            '::selection': {
              backgroundColor: alpha(currentTheme.palette.primary.main, 0.3),
            },
            '*::-webkit-scrollbar': { width: '10px', height: '10px' },
            '*::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(currentTheme.palette.text.primary, 0.18),
              borderRadius: '999px',
              border: '3px solid transparent',
              backgroundClip: 'padding-box',
            },
            '@font-face': {
              fontFamily: 'Chainz',
              src: `url('/fonts/ChainzG98-B0an.ttf') format('truetype')`, // Correctly formatted
              fontWeight: 'normal',
              fontStyle: 'normal',
            },
          }}
        />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

const commonThemeAttributes = {
  shadows: [...createTheme().shadows],
  shape: { borderRadius: 16 },
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    button: { fontWeight: 750, letterSpacing: '0.01em' },
    h6: { fontWeight: 800 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '14px',
          minHeight: '44px',
          paddingInline: '18px',
          boxShadow: 'none',
          textTransform: 'none',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: '12px' },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { padding: '10px' },
        thumb: { boxShadow: '0 2px 8px rgba(0,0,0,.22)' },
      },
    },
  },
  customShadows: {
    shadowColor: 'rgba(0, 0, 0, 0.1)',
  },
  opacities: {
    lightOverlay: 'rgba(0, 0, 0, 0.7)', 
  },
  custom: {
    letterBorder: '#000000',
    letterBoxBorder: '#000000',
    kbBtnBg: '#efefef',
    kbColor: '#000000',
    snackbar: {
      success: '#3d9958',
      error: '#c84153',
      warning: '#e6c000',
    },
    letterRow: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'row',
      padding: '6px 0',
    },
    letterBox: {
      borderRadius: '13px',
      margin: '5px',
      fontSize: 'clamp(1.65rem, 7vw, 2.4rem)',
      fontWeight: 800,
      height: 'clamp(2.75rem, 13vw, 3.65rem)',
      width: 'clamp(2.75rem, 13vw, 3.65rem)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textTransform: 'uppercase',
      backgroundImage:
        'linear-gradient(145deg, rgba(255, 255, 255, 0.24), transparent 58%, rgba(15, 23, 42, 0.035))',
      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.12)',
      transition: 'transform 180ms ease, box-shadow 180ms ease',
    },
  },
};

// Light Theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: '#f4f7fb',
      paper: '#ffffff',
    },
    primary: {
      main: '#258f8b',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#6d5bd0',
      contrastText: '#ffffff',
    },
    text: {
      primary: '#172033',
      secondary: '#667085',
    },
  },
  ...commonThemeAttributes,
  custom: {
    ...commonThemeAttributes.custom,
    tertiary: '#b6efb6',
    incorrect: '#d7dde8',
    aura: 'rgba(245, 158, 11, .78)',
    surface: 'rgba(255, 255, 255, 0.78)',
    surfaceElevated: '#ffffff',
    border: 'rgba(23, 32, 51, 0.10)',
    mutedText: '#667085',
  },
});

// Dark Theme
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0b1020',
      paper: '#151b2d',
    },
    primary: {
      main: '#65c7c2',
      contrastText: '#071312',
    },
    secondary: {
      main: '#9b8afb',
      contrastText: '#100c26',
    },
    text: {
      primary: '#ffffff',
    },
  },
  ...commonThemeAttributes,
  customShadows: {
    shadowColor: 'rgba(255, 255, 255, 0.1)',
  },
  custom: {
    ...commonThemeAttributes.custom,
    tertiary: '#d6d66f',
    incorrect: '#30394e',
    aura: 'rgb(214, 214, 111)',
    letterBorder: '#3a3a3a',
    letterBoxBorder: '#5f6b83',
    kbBtnBg: '#252d42',
    kbColor: '#ffffff',
    surface: 'rgba(21, 27, 45, 0.82)',
    surfaceElevated: '#1b2338',
    border: 'rgba(255, 255, 255, 0.10)',
    mutedText: '#a9b2c5',
  },
});

// Color Blind Light Theme
export const colorBlindLightTheme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: '#f5f7fb',
      paper: '#ffffff',
    },
    primary: {
      main: '#0c7bdc',
    },
    secondary: {
      main: '#ffc20a',
    },
    text: {
      primary: '#000000',
    },
  },
  ...commonThemeAttributes,
  custom: {
    ...commonThemeAttributes.custom,
    tertiary: '#228b22',
    incorrect: '#808080',
    aura: 'rgb(34, 139, 34)',
    surface: 'rgba(255, 255, 255, 0.82)',
    surfaceElevated: '#ffffff',
    border: 'rgba(0, 0, 0, 0.12)',
    mutedText: '#596273',
  },
});

// Color Blind Dark Theme
export const colorBlindDarkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0b1020',
      paper: '#151b2d',
    },
    primary: {
      main: '#5d3a9b',
    },
    secondary: {
      main: '#e66100',
    },
    text: {
      primary: '#ffffff',
    },
  },
  ...commonThemeAttributes,
  customShadows: {
    shadowColor: 'rgba(255, 255, 255, 0.1)',
  },
  custom: {
    ...commonThemeAttributes.custom,
    tertiary: '#d6d66f',
    incorrect: '#3a3a3a',
    aura: 'rgb(214, 214, 111)',
    letterBorder: '#3a3a3a',
    letterBoxBorder: '#808080',
    kbBtnBg: '#818384',
    kbColor: '#ffffff',
    surface: 'rgba(21, 27, 45, 0.84)',
    surfaceElevated: '#1b2338',
    border: 'rgba(255, 255, 255, 0.12)',
    mutedText: '#b5bdd0',
  },
});

// Contrast Light Theme
export const contrastLightTheme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: '#f4f7fb',
      paper: '#ffffff',
    },
    primary: {
      main: '#7bb8f3',
    },
    secondary: {
      main: '#f46928',
    },
    text: {
      primary: '#000000',
    },
  },
  ...commonThemeAttributes,
  custom: {
    ...commonThemeAttributes.custom,
    tertiary: '#ffff00',
    incorrect: '#808080',
    aura: 'rgb(34, 139, 34)',
    surface: 'rgba(255, 255, 255, 0.86)',
    surfaceElevated: '#ffffff',
    border: 'rgba(0, 0, 0, 0.18)',
    mutedText: '#3f4652',
  },
});

// Contrast Dark Theme
export const contrastDarkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#080b12',
      paper: '#141923',
    },
    primary: {
      main: '#7bb8f3',
    },
    secondary: {
      main: '#f46928',
    },
    text: {
      primary: '#ffffff',
    },
  },
  ...commonThemeAttributes,
  customShadows: {
    shadowColor: 'rgba(255, 255, 255, 0.1)',
  },
  custom: {
    ...commonThemeAttributes.custom,
    tertiary: '#cacb60',
    incorrect: '#3a3a3a',
    aura: 'rgb(214, 214, 111)',
    letterBorder: '#3a3a3a',
    letterBoxBorder: '#808080',
    kbBtnBg: '#818384',
    kbColor: '#ffffff',
    surface: 'rgba(20, 25, 35, 0.88)',
    surfaceElevated: '#1b2230',
    border: 'rgba(255, 255, 255, 0.18)',
    mutedText: '#c1c7d2',
  },
});
